/** @jsx jsx */
import { useState, useReducer, useCallback, Reducer } from 'react';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';

import { ChallengesType, ICustomTheme } from 'interfaces';
import { ProductAPI } from 'services/product.api';
import Layout from '../StepLayout';
import Filter from './Filter';
import MobileFilterLayout from './MobileFilterLayout';
import ProductCardsList from './ProductCardsList';
import { getFormattedFilter } from './utils';
import { STEPS } from '../constants';

import filterReducer, {
  initialState,
  StateType,
  ActionType,
} from './filter.reducer';

export const DeclarationFormProductsStep = ({
  challengeListProducts,
  needCurrencyValue,
}: ChallengesType): JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );
  const { id: challengeId } = useParams<{ id: string }>();
  const [openDialog, setOpenDialog] = useState(false);
  const [products, setProducts] = useState(challengeListProducts);
  const [filters, filtersDispatch] = useReducer<Reducer<StateType, ActionType>>(
    filterReducer,
    initialState,
  );

  const getProducts = useCallback(
    () =>
      ProductAPI.getChallengeProducts(
        {
          search: filters.search,
          brands: getFormattedFilter(filters.brands),
          ranges: getFormattedFilter(filters.ranges),
        },
        +challengeId,
      )
        .then(({ data }: any) => setProducts(data?.['hydra:member']))
        .catch(console.error),
    [setProducts, challengeId, filters.brands, filters.ranges, filters.search],
  );

  return (
    <Layout
      title={t('challenges.declaration.productsDeclaration')}
      subtitle={t('challenges.declaration.selectYourProducts')}
      step={STEPS.products}
    >
      {isMobile ? (
        <MobileFilterLayout
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
        >
          <Filter
            challengeId={challengeId}
            filters={filters}
            filtersDispatch={filtersDispatch}
            numberOfProducts={products?.length}
            getProducts={getProducts}
            onConfirm={() => setOpenDialog(false)}
            isMobile
          />
        </MobileFilterLayout>
      ) : (
        <Filter
          challengeId={challengeId}
          filters={filters}
          filtersDispatch={filtersDispatch}
          numberOfProducts={products?.length}
          getProducts={getProducts}
          isMobile={false}
        />
      )}
      <ProductCardsList
        products={products}
        isValueFieldRequired={needCurrencyValue}
      />
    </Layout>
  );
};
